// Third-party
import React from 'react';
import classNames from 'classnames';

// Components
import NoDataCell from './NoDataCell';
import { HoldingIcon } from '../common';

// Utils
import { formatPercentage, formatNumber, formatMessageWithLocale } from '../../../utils/i18n';
import { PROPOSAL_ITEM_PURPOSE } from '@/modules/proposal/utils/constants';
import RiskRewardLegendValue from '@/types/risk-reward-legend-value';
import Locale from '@/types/locale';

type Props = {
  value?: RiskRewardLegendValue;
  className?: string;
  itemType: string;
  color: string;
  flags?: string[];
  locale?: Locale;
};

const getValue = (value: RiskRewardLegendValue, locale?: Locale) => (
  <div className="item-value">
    <div className="d-flex justify-content-between">
      <span className="label">{formatMessageWithLocale('RiskRewardPlot.Tooltip.LabelX', locale)}</span>

      <span className={classNames('x-value', { 'no-value': value.stdDev == null })}>
        {value.stdDev != null ? formatNumber(value.stdDev, 2, locale) : formatMessageWithLocale('Common.NotApplicable', locale)}
      </span>
    </div>

    <div className="d-flex justify-content-between legend-return">
      <span className="label">{formatMessageWithLocale('RiskRewardPlot.Tooltip.LabelY', locale)}</span>

      <span className={classNames('y-value', { 'no-value': value.return == null })}>
        {value.return != null ? `${formatPercentage(value.return, 2, locale)}` : formatMessageWithLocale('Common.NotApplicable', locale)}
      </span>
    </div>
  </div>
);

const RiskRewardLegendCell: React.FunctionComponent<Props> = ({ value, locale, className, color, itemType, flags }: Props) => {
  const purposeFlag = Object.values(PROPOSAL_ITEM_PURPOSE).find(flag => flags?.includes(flag));

  if (value == null) {
    return <NoDataCell />;
  }

  return (
    <div className={classNames('cell risk-reward-legend-cell', className, { 'font-weight-bold': purposeFlag === PROPOSAL_ITEM_PURPOSE.RECOMMENDED })}>
      <div className="item-label">
        <div className="cell-icon-wrapper no-shrink" style={{ '--holding-accent-color': color } as React.CSSProperties}>
          <HoldingIcon type={itemType} color={color} className={purposeFlag} />
        </div>

        <span className="item-name">{value.name}</span>
      </div>
      <div className="legend-item-value">{getValue(value, locale)}</div>
    </div>
  );
};

export default RiskRewardLegendCell;
